"use client";

import { useEffect, useState } from "react";
import crypto from "crypto";
import { toast } from "sonner";

// Local imports
import { useAdministrationContext } from "./administration_context";
import { post } from "../../lib/api";
import { useSidebar } from "../ui/sidebar";
import LoadingScreen from "../Unique/Animation/loading_screen";

// shadcn/ui components
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Separator } from "../../components/ui/separator";
import { InfoIcon } from "lucide-react";

const UserManager = () => {
  const { user, setUser } = useAdministrationContext(); // user = [user_id, username]
  const { mode } = useSidebar();

  // Local state
  const [password, setPassword] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper for dynamic input styles based on light/dark mode
  const inputClass = mode === "light" ? "border border-gray-300 rounded px-3 py-2 focus:outline-none" : "border border-gray-600 bg-gray-700 text-white rounded px-3 py-2 focus:outline-none";

  // Fetch all users
  useEffect(() => {
    const fetchData = async () => {
      const getUsers = await post({ method: "getUsers" });
      setUsers(getUsers);
      setLoading(false);
    };
    fetchData();
  }, []);

  // Warn user once users are loaded
  useEffect(() => {
    if (users.length > 0) {
      toast.warning(`Changed to user ${user[1]} (ID: ${user[0]})! Any changes made in Page Manager or Change password are made for this user!`);
    }
  }, [users, user]);

  // Handle selection from the drop-down
  const handleUserChange = (selectedValue) => {
    const selectedUser = users.find((u) => u.user_id === selectedValue);
    if (selectedUser) {
      setUser([Number.parseInt(selectedUser.user_id), selectedUser.username]);
    }
  };

  // Input handlers
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };
  const handleNewUsernameChange = (e) => {
    setNewUsername(e.target.value);
  };
  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
  };

  // Hash a password using sha256
  const hashPassword = (passwordToHash) => {
    const hash = crypto.createHash("sha256");
    hash.update(passwordToHash);
    return hash.digest("hex");
  };

  // Add a new user
  const handleAddUser = async () => {
    if (newUsername.length < 1 || newPassword.length < 1) {
      toast.error("Both username and password need to have entries!");
      return;
    }

    const loadingToastId = toast.loading("Adding new user...");

    try {
      const hashedPassword = hashPassword(newPassword);

      // Await the response to ensure it completes before proceeding
      const response = await post({ method: "addUser", username: newUsername, password: hashedPassword });

      if (response.status === "error") {
        throw new Error(response.message);
      }

      toast.success(`User ${newUsername} added!`);
      setUsers([...users, { user_id: response.user_id, username: newUsername }]);
      setNewUsername("");
      setNewPassword("");
    } catch (error) {
      toast.error("Failed to add new user!");
    } finally {
      toast.dismiss(loadingToastId);
    }
  };

  // Change the current user's password
  const handleChangePassword = async () => {
    if (password.length < 1) {
      toast.error("Password needs to have an entry!");
      return;
    }

    const loadingToastId = toast.loading("Changing password...");

    try {
      const hashedPassword = hashPassword(password);

      // Ensure the request is awaited
      const response = await post({ method: "changePassword", user, password: hashedPassword });

      if (response.status === "error") {
        throw new Error(response.message);
      }

      toast.success(`Password for user ${user[1]} changed!`);
      setPassword("");
    } catch (error) {
      toast.error("Failed to change password!");
    } finally {
      toast.dismiss(loadingToastId);
    }
  };

  // Show loading screen until users are fetched
  if (loading) {
    return <LoadingScreen text="Loading..." />;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      {/* Left Column - User Selection and Password Change */}
      <div className="space-y-6">
        {/* USER SELECTION SECTION */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Select User</h2>
          <div className="max-w-md">
            <div className="flex flex-col gap-1 w-1/2">
              <Select value={String(user[0])} onValueChange={handleUserChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select User" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.user_id} value={String(u.user_id)}>
                      {u.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="text-sm text-gray-500 flex items-center gap-1">
            <InfoIcon className="h-4 w-4" />
            Changing user will allow you to change password and the pages assigned to that user.
          </div>
        </div>

        <Separator className="my-6" />

        {/* CHANGE PASSWORD SECTION */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Change Password</h2>
          <div className="max-w-md">
            <div className="grid grid-cols-2 gap-4">
              {/* New Password Input */}
              <div className="flex flex-col gap-1">
                <input type="password" className={`${inputClass} w-full h-9`} value={password} onChange={handlePasswordChange} />
              </div>
              <div className="flex items-end">
                <Button className="bg-purple-700 hover:bg-purple-800 dark:text-white" onClick={handleChangePassword}>
                  Submit
                </Button>
              </div>
            </div>
          </div>
          <div className="text-sm text-gray-500 flex items-center gap-1">
            <InfoIcon className="h-4 w-4" />
            Change password for selected user.
          </div>
        </div>
      </div>

      {/* Right Column - Add New User */}
      <div className="space-y-4 sm:border-l sm:pl-8">
        <h2 className="text-xl font-semibold">Add New User</h2>
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4">
            {/* New Username */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Username</label>
              <input className={`${inputClass} w-full h-9`} value={newUsername} onChange={handleNewUsernameChange} />
            </div>

            {/* New Password */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Password</label>
              <input type="password" className={`${inputClass} w-full h-9`} value={newPassword} onChange={handleNewPasswordChange} />
            </div>

            <div>
              <Button className="bg-purple-700 hover:bg-purple-800 dark:text-white w-full" onClick={handleAddUser}>
                Add New User
              </Button>
            </div>
          </div>

          <div className="text-sm text-gray-500 flex items-center gap-1">
            <InfoIcon className="h-4 w-4" />
            Adding a new user will allow you to assign pages to that user.
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManager;
